import React from 'react';
import styles from './region-entity-table.module.scss';
import { connect } from 'react-redux';
import * as selectors from '../../reducers';
import PropTypes from 'prop-types';
import {
  setActiveEntity,
  setComparedEntity,
  setActivePage,
} from '../../actions/ui';
import { RegionTableHeader } from './region-table-header';
import { ArrowRight } from '../../components/icons';

const prettyDisplay = (val) => {
  return val ? val.toLocaleString() : '-';
};

const RegionTableComponent = ({
  region,
  setActiveEntity,
  setComparedEntity,
  setActivePage,
  isMobile,
}) => {
  const waterEntityCols = [
    {
      header: 'Water entity',
      key: 'entity',
      minWidth: 200,
      display: prettyDisplay,
      onClick: (entity) => {
        setActiveEntity(entity);
        setComparedEntity(null);
        setActivePage('entity');
      },
    },
    { header: 'Capacity (ML)', key: 'capacityMl', display: prettyDisplay },
    { header: 'Storage facilities', key: 'dams', display: prettyDisplay },
    {
      header: 'Storage level (30 June 2020)',
      key: 'byRegionByEntityByFacilityData',
      display: (e) => (
        <div>
          {e.length
            ? e
                .sort((a, b) => a.storageLevel - b.storageLevel)
                .map((e, i) => storageLevels(e, i))
            : '-'}
        </div>
      ),
    },
  ];

  const councilCols = [
    {
      header: 'Council owned storage facilities',
    },
    { header: 'Capacity (ML)' },
    {
      header: 'Storage level (30 June 2020)',
    },
  ];

  const hasDisplayData = (entityObj) => {
    return entityObj.capacityMl || entityObj.dams || entityObj.storageLevel;
  };
  const hasNonCouncilFacilities =
    region.byRegionByEntity.filter(
      (x) => x.entity !== 'Council' && hasDisplayData(x)
    ).length > 0;
  const hasCouncilFacilities =
    region.byRegionByCouncilByFacilityData.length > 0;
  const hasFacilities = hasNonCouncilFacilities || hasCouncilFacilities;

  return (
    <div style={{ width: '100%' }}>
      <div style={{ width: '100%', overflow: 'auto' }}>
        <table className={styles.entityRegionTable}>
          <tbody>
            <RegionTableHeader
              region={region}
              comparing={false}
              isMobile={isMobile}
            />
            {hasNonCouncilFacilities && (
              <tr className={styles.regionTableHeaderRow}>
                {waterEntityCols.map((c) => (
                  <td key={`header-${c.key}`}>{c.header}</td>
                ))}
              </tr>
            )}
            {hasNonCouncilFacilities &&
              region.byRegionByEntity
                .filter((x) => x.entity !== 'Council')
                .map((e, i) => {
                  return (
                    <tr className={styles.regularTableRow} key={e + i}>
                      {waterEntityCols.map((c) => {
                        const value = e[c.key];
                        return (
                          <td
                            key={c.key}
                            style={{ minWidth: c.minWidth }}
                            data-clickable={c.onClick !== undefined}
                            onClick={() => {
                              c.onClick(value);
                            }}
                          >
                            {c.display(value)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
            {hasCouncilFacilities && (
              <tr className={styles.regionTableHeaderRow}>
                {councilCols.map((col) => (
                  <td>{col.header}</td>
                ))}
              </tr>
            )}
            {region.byRegionByCouncilByFacilityData.map((e, i) => {
              const hasDam = e.dam !== '';
              const hasCapacity = e.capacityMl > 0;
              const hasStorageLevel =
                e.storageLevel !== '' && e.storageLevel !== null;
              return (
                <tr className={styles.regularTableRow} key={e + i}>
                  <td style={{ minWidth: 200 }} data-clickable={false}>
                    {hasDam ? e.dam.toLocaleString() : '-'}
                  </td>
                  <td className={styles.numberCell}>
                    {hasCapacity
                      ? e.capacityMl.toLocaleString()
                      : 'Information not publicly available'}
                  </td>
                  <td className={styles.numberCell}>
                    {hasStorageLevel
                      ? e.storageLevel.toLocaleString()
                      : 'Information not publicly available'}
                  </td>
                </tr>
              );
            })}
            {region.byRegionByEntity
              .filter(
                (x) =>
                  x.secondaryEntity != null &&
                  x.secondaryEntity !== '' &&
                  x.secondaryEntityType !== 'Council'
              )
              .map((e, i) => {
                return (
                  <tr className={styles.regularTableRow} key={e + i}>
                    <td
                      style={{ minWidth: 200 }}
                      onClick={() => {
                        setActiveEntity(e.secondaryEntity);
                        setComparedEntity(null);
                        setActivePage('entity');
                      }}
                      data-clickable={true}
                    >
                      {e.secondaryEntity}{' '}
                      {e.secondaryEntityType !== '' &&
                        '(' + e.secondaryEntityType + ')'}
                    </td>
                    <td className={styles.numberCell}></td>
                    <td className={styles.numberCell}></td>
                    <td className={styles.numberCell}></td>
                    <td className={styles.numberCell}></td>
                    <td className={styles.numberCell}></td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      {isMobile &&
        region.byRegionByEntity.filter((x) => x.entity !== 'Council').length >
          0 && (
          <div style={{ textAlign: 'left', marginTop: 20 }}>
            <span className={styles.cellInfoText}>
              Scroll the table to see more
            </span>
            <ArrowRight
              fill="blue"
              width="10px"
              height="10px"
              style={{ marginLeft: 10 }}
            />
          </div>
        )}
      {hasFacilities && (
        <div>
          <span className={styles.cellInfoText}>
            Water for this region may be supplied by another facility.
          </span>
        </div>
      )}
      {!hasFacilities && (
        <div>
          <span className={styles.cellInfoText}>
            Information not publicly available.
          </span>
        </div>
      )}
    </div>
  );
};

const storageLevels = (facility, i) => {
  return (
    <p className={styles.subRow} key={facility + i}>
      {facility.dam} ({(facility.storageLevel * 100).toFixed(1)}%)
    </p>
  );
};

const mapDispatchToProps = {
  setActiveEntity,
  setComparedEntity,
  setActivePage,
};

const mapStateToProps = (state, props) => ({
  isMobile: selectors.getIsMobile(state),
});

RegionTableComponent.propTypes = {
  region: PropTypes.object,
  setActiveEntity: PropTypes.func,
  setActivePage: PropTypes.func,
  setComparedEntity: PropTypes.func,
};

const RegionTable = connect(
  mapStateToProps,
  mapDispatchToProps
)(RegionTableComponent);
export { RegionTable };



// WEBPACK FOOTER //
// ./src/components/region-entity-table/region-table.js